/* ============================================================================
   LX LIGHTBOX — fullscreen photo enlarge (product page gallery + quickshop).
   ----------------------------------------------------------------------------
   Public API (exposed as BOTH window.LxLightbox and window.LXLightbox):
     open(items, index)  items: [{ type:'image'|'video', src, poster, alt,
                         srcset?, sizes? }]. `type` defaults to 'image', and
                         image items keep the legacy { src, srcset, sizes, alt }
                         shape for back-compat, so existing callers are unchanged.
                         Video items render an inline <video controls> slide
                         (no zoom); image items keep click/scroll/pinch zoom.
                         Loop, drag-swipe and backdrop-close apply to every item;
                         a slide change always resets zoom (v5.4).
     chip(hostEl)        appends the magnifier affordance chip to hostEl and
                         tags it .lx-lb-zoomhost (hover reveal / touch subtle).

   Shell: ONE shared native <dialog class="lx-lb"> built on first open.
   showModal() puts it in the top layer ABOVE the quickshop dialog, Esc only
   reaches the topmost modal, and focus trapping + restore are native.
   Swipe: touch keeps the native scroll-snap rail; desktop mouse/pen gets a JS
   drag-swipe (live scrollLeft follow, commit on release). Both share the same
   rect-delta geometry as the quickshop rail, so RTL works with zero special
   cases. Arrows/dots/drag all LOOP (next past the last wraps to the first).

   Auto-wiring (product page): [data-lx-gallery] image slides get the chip and
   click-to-open. lx-product.js pointer-captures the gallery viewport (drag),
   which retargets clicks to the viewport — so we record the pointerdown
   target + travel ourselves and open only on clean clicks over image slides.
   Quickshop wires itself via the public API (see lx-quickshop.js).
   ========================================================================= */
(function () {
  'use strict';
  if (window.__lxLightboxLoaded) return;
  window.__lxLightboxLoaded = true;

  var REDUCED = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : { matches: false };

  var SVG = {
    close:
      '<svg viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M1.5 1.5l11 11M12.5 1.5l-11 11"/></svg>',
    chevron:
      '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M6 3l5 5-5 5"/></svg>',
    zoom:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><circle cx="10.5" cy="10.5" r="6.5"/><path d="M15.4 15.4 20.5 20.5M10.5 8v5M8 10.5h5"/></svg>',
    play:
      '<svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M8 5v14l11-7z"/></svg>'
  };

  function readConfig() {
    var el = document.querySelector('[data-lx-lb-config]');
    var cfg = { close: 'Close', prev: 'Previous slide', next: 'Next slide', slide: 'Slide', zoom: 'Zoom', action: 'See it in Action' };
    if (el) {
      try {
        var parsed = JSON.parse(el.textContent);
        Object.keys(parsed).forEach(function (k) {
          if (typeof parsed[k] === 'string' && parsed[k]) cfg[k] = decodeEntities(parsed[k]);
        });
      } catch (e) {
        /* fall back to English defaults */
      }
    }
    return cfg;
  }

  function decodeEntities(s) {
    if (!s || s.indexOf('&') === -1) return s;
    var box = document.createElement('textarea');
    box.innerHTML = s;
    return box.value;
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  var dlg = null;
  var rail = null;
  var dotsBox = null;
  var prevBtn = null;
  var nextBtn = null;
  var actionBtn = null;
  var state = null; // { slides:[], dots:[], active, count }
  var closing = false;
  var T = null;
  var ownsBodyLock = false; // don't unlock a scroll lock the quickshop owns

  /* ---- zoom/pan state (active slide only; slide-change resets) ------------- */
  var Z_MAX = 4;          // ceiling for wheel/pinch
  var Z_TAP = 2.5;        // level a click/tap zooms to
  var Z_TAP_MOVE = 8;     // px: tap vs drag/swipe threshold
  var Z_EASE = 'transform 240ms cubic-bezier(0.4,0,0.2,1)';
  var z = null;           // { img, s, tx, ty, ...gesture fields } bound to active img
  var zPointers = new Map();
  var swallowNextClick = false; // eat the click that trails a pan/pinch/swipe

  /* ---- desktop drag-swipe + drag-safe backdrop -----------------------------
     Touch keeps the native scroll-snap rail (touch-action:pan-x on the image);
     mouse/pen — which cannot drag a scroll container natively — get this JS
     swipe: live scrollLeft follow (physical, so LTR/RTL are identical), commit
     to prev/next on release past a threshold, otherwise settle back. */
  var lbSwipe = null;       // { id, startX, startY, startScroll, moved }
  var lbDownWash = false;   // true only when a pointerdown LANDED on the wash

  function ensureShell() {
    if (dlg) return;
    T = readConfig();
    dlg = document.createElement('dialog');
    dlg.className = 'lx-lb';
    dlg.setAttribute('aria-label', T.zoom);
    dlg.innerHTML =
      '<div class="lx-lb__rail" data-lb-rail></div>' +
      '<button type="button" class="lx-lb__close" data-lb-close aria-label="' + esc(T.close) + '">' + SVG.close + '</button>' +
      '<button type="button" class="lx-lb__nav lx-lb__nav--prev" data-lb-prev aria-label="' + esc(T.prev) + '">' + SVG.chevron + '</button>' +
      '<button type="button" class="lx-lb__nav lx-lb__nav--next" data-lb-next aria-label="' + esc(T.next) + '">' + SVG.chevron + '</button>' +
      '<div class="lx-lb__dots" data-lb-dots role="tablist"></div>' +
      '<button type="button" class="lx-lb__action" data-lb-action hidden>' + SVG.play + '<span>' + esc(T.action) + '</span></button>';
    document.body.appendChild(dlg);

    rail = dlg.querySelector('[data-lb-rail]');
    dotsBox = dlg.querySelector('[data-lb-dots]');
    prevBtn = dlg.querySelector('[data-lb-prev]');
    nextBtn = dlg.querySelector('[data-lb-next]');
    actionBtn = dlg.querySelector('[data-lb-action]');

    // "See it in Action" → close the lightbox, then open the MediaBlazer reels
    // at its first tile (as if the shopper clicked that tile themselves).
    actionBtn.addEventListener('click', function () {
      var tile = document.querySelector('[data-mediablazer] [data-mb-tile]');
      close();
      if (tile) setTimeout(function () { try { tile.click(); } catch (e) {} }, 260);
    });

    dlg.querySelector('[data-lb-close]').addEventListener('click', close);
    dlg.addEventListener('cancel', function (e) {
      e.preventDefault();
      close();
    });
    // wash click (anywhere that is not the media or a control) closes — BUT
    // only when the pointerDOWN also started on the wash, so a drag that begins
    // on the photo (pan/swipe) and happens to end on the wash never closes.
    dlg.addEventListener(
      'pointerdown',
      function (e) {
        lbDownWash = !(e.target.closest && e.target.closest('img, video, button, [data-lb-dots]'));
      },
      true
    );
    dlg.addEventListener('click', function (e) {
      if (e.target.closest('img, video, button, [data-lb-dots]')) return;
      if (!lbDownWash) return; // press started on the media → treat as a drag
      close();
    });
    dlg.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        arrowStep(-1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        arrowStep(1);
      }
    });
    prevBtn.addEventListener('click', function () {
      goTo(activeIndex() - 1);
    });
    nextBtn.addEventListener('click', function () {
      goTo(activeIndex() + 1);
    });
    var raf = null;
    rail.addEventListener('scroll', function () {
      if (raf) return;
      raf = requestAnimationFrame(function () {
        raf = null;
        syncActive();
      });
    });

    // Touch swipe-loop: on mobile the side arrows are hidden, so a decisive swipe
    // at a boundary (where the native scroll-snap rail can't move further) wraps
    // to the other end. Middle swipes never reach here (the rail moves → return).
    // Boundary is read from the logical active index, so it stays RTL-safe.
    var tsx = 0, tss = 0, tsActive = false;
    rail.addEventListener('touchstart', function (e) {
      if (e.touches.length !== 1) { tsActive = false; return; }
      tsx = e.touches[0].clientX;
      tss = rail.scrollLeft;
      tsActive = true;
    }, { passive: true });
    rail.addEventListener('touchend', function (e) {
      if (!tsActive) return;
      tsActive = false;
      if (!state || state.count <= 1) return;
      if (dlg.classList.contains('is-zoomed')) return;       // zoom/pan, not a swipe
      if (Math.abs(rail.scrollLeft - tss) > 5) return;       // rail moved → native handled it
      var t = e.changedTouches && e.changedTouches[0];
      var dx = t ? t.clientX - tsx : 0;
      if (Math.abs(dx) < 50) return;                         // not a decisive swipe
      var rtl = getComputedStyle(document.documentElement).direction === 'rtl';
      var logical = rtl ? dx : -dx;                          // + = advance (next)
      if (logical > 0 && state.active >= state.count - 1) goTo(0);
      else if (logical < 0 && state.active <= 0) goTo(state.count - 1);
    }, { passive: true });

    // selection hygiene on the arrows (recon #1): a rapid double-click on an
    // arrow must never seed a browser selection that paints the dark wash.
    [prevBtn, nextBtn, dlg.querySelector('[data-lb-close]')].forEach(function (b) {
      if (b) b.addEventListener('pointerdown', function (e) { e.preventDefault(); });
    });

    initZoom();
  }

  /* ========================================================================= */
  /* Zoom + pan (additive; leaves backdrop/Esc/close/dots/arrows untouched)    */
  /* ========================================================================= */

  function initZoom() {
    rail.addEventListener('click', onZoomClick);
    rail.addEventListener('dblclick', onZoomDblClick);
    rail.addEventListener('pointerdown', onZoomPointerDown);
    rail.addEventListener('pointermove', onZoomPointerMove, { passive: false });
    rail.addEventListener('pointerup', onZoomPointerUp);
    rail.addEventListener('pointercancel', onZoomPointerUp);
    rail.addEventListener('wheel', onZoomWheel, { passive: false });
    // capture-phase: swallow the click that trails a pan/pinch/swipe so it
    // neither toggles zoom (rail handler) nor closes (dlg wash handler).
    dlg.addEventListener(
      'click',
      function (e) {
        if (swallowNextClick) {
          swallowNextClick = false;
          e.stopPropagation();
          e.preventDefault();
        }
      },
      true
    );
  }

  function zActiveImg() {
    if (!state || !state.slides[state.active]) return null;
    return state.slides[state.active].querySelector('img');
  }

  function zClearImg(img) {
    img.style.transform = '';
    img.style.transition = '';
    img.classList.remove('lx-lb__img--zoomed', 'is-grabbing');
  }

  // bind z to the current active image (rebinding clears the previous one)
  function zEnsure() {
    var img = zActiveImg();
    if (!img) return null;
    if (!z || z.img !== img) {
      if (z && z.img && z.img !== img) zClearImg(z.img);
      z = { img: img, s: 1, tx: 0, ty: 0, panId: null, moved: false, didPinch: false, pinch: null };
    }
    return z;
  }

  function zApply(animate) {
    if (!z || !z.img) return;
    z.img.style.transition = animate && !REDUCED.matches ? Z_EASE : 'none';
    z.img.style.transform = 'translate3d(' + z.tx + 'px,' + z.ty + 'px,0) scale(' + z.s + ')';
  }

  function zSetZoomed(on) {
    dlg.classList.toggle('is-zoomed', on);
    if (z && z.img) z.img.classList.toggle('lx-lb__img--zoomed', on);
  }

  // derive fit metrics live from the current rendered rect + known transform,
  // so it stays correct across resize/orientation without stale capture.
  function zMetrics() {
    var r = z.img.getBoundingClientRect();
    var s = z.s || 1;
    return {
      bw: r.width / s,
      bh: r.height / s,
      cx: r.left + r.width / 2 - z.tx,
      cy: r.top + r.height / 2 - z.ty
    };
  }

  // clamp translate so the photo can never be dragged off its own edges
  // (covers the viewport when larger; re-centres per axis when smaller).
  function zClamp(tx, ty, s, m) {
    var R = rail.getBoundingClientRect();
    var hw = (m.bw * s) / 2;
    var hh = (m.bh * s) / 2;
    var cx = m.cx + tx;
    var cy = m.cy + ty;
    if (2 * hw <= R.width) cx = R.left + R.width / 2;
    else cx = Math.min(R.left + hw, Math.max(R.left + R.width - hw, cx));
    if (2 * hh <= R.height) cy = R.top + R.height / 2;
    else cy = Math.min(R.top + hh, Math.max(R.top + R.height - hh, cy));
    return { tx: cx - m.cx, ty: cy - m.cy };
  }

  // focal zoom to newS about screen point (fx,fy) — keeps that point put.
  function zTo(newS, fx, fy, animate) {
    if (!z || !z.img) return;
    newS = Math.max(1, Math.min(Z_MAX, newS));
    var m = zMetrics();
    var ratio = newS / (z.s || 1);
    var tx = z.tx * ratio + (fx - m.cx) * (1 - ratio);
    var ty = z.ty * ratio + (fy - m.cy) * (1 - ratio);
    var c = zClamp(tx, ty, newS, m);
    z.s = newS;
    z.tx = c.tx;
    z.ty = c.ty;
    zApply(animate);
    zSetZoomed(z.s > 1.01);
  }

  function zReset(animate) {
    if (!z) return;
    z.s = 1;
    z.tx = 0;
    z.ty = 0;
    z.panId = null;
    z.pinch = null;
    zApply(animate);
    zSetZoomed(false);
    if (z.img) z.img.classList.remove('is-grabbing');
  }

  // full stop for open/close so a reused shell never inherits a zoom
  function zTeardown() {
    if (z && z.img) zClearImg(z.img);
    z = null;
    zPointers.clear();
    swallowNextClick = false;
    if (dlg) dlg.classList.remove('is-zoomed');
  }

  function zToggle(fx, fy) {
    if (!zEnsure()) return;
    if (z.s > 1.01) zReset(true);
    else zTo(Z_TAP, fx, fy, true);
  }

  function overImage(e) {
    return !!(e.target && e.target.closest && e.target.closest('.lx-lb__slide img'));
  }

  function onZoomClick(e) {
    if (!overImage(e)) return;
    if (e.detail >= 2) return; // 2nd click of a dblclick — dblclick handler resets
    zToggle(e.clientX, e.clientY);
  }

  function onZoomDblClick(e) {
    if (!overImage(e)) return;
    e.preventDefault();
    if (!zEnsure()) return;
    zReset(true);
  }

  function onZoomPointerDown(e) {
    var slide = e.target && e.target.closest ? e.target.closest('.lx-lb__slide') : null;
    if (!slide) return;
    var img = e.target.closest ? e.target.closest('.lx-lb__slide img') : null;

    /* MOUSE / PEN — single-pointer devices, no pinch. When the photo is zoomed
       a drag PANS; at fit (image OR video slide) a drag SWIPES. Native scroll-
       snap doesn't react to a mouse drag, which is why we drive it here. */
    if (e.pointerType !== 'touch') {
      if (img && z && z.s > 1.01 && zEnsure()) {
        zPointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
        swallowNextClick = false;
        z.startX = e.clientX;
        z.startY = e.clientY;
        z.moved = false;
        z.didPinch = false;
        z.panId = e.pointerId;
        z.panTx = z.tx;
        z.panTy = z.ty;
        z.panX = e.clientX;
        z.panY = e.clientY;
        try {
          img.setPointerCapture(e.pointerId);
        } catch (_) {}
        img.classList.add('is-grabbing');
        return;
      }
      swallowNextClick = false;
      lbSwipeStart(e);
      return;
    }

    /* TOUCH — existing zoom/pan/pinch pipeline (image slides only). At fit the
       native scroll-snap rail owns the horizontal swipe. */
    if (!img) return;
    if (!zEnsure()) return;
    zPointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (zPointers.size === 1) {
      // fresh gesture: any stale swallow from a click-less touch pan is void
      // (a real trailing click always fires before the next pointerdown).
      swallowNextClick = false;
      z.startX = e.clientX;
      z.startY = e.clientY;
      z.moved = false;
      z.didPinch = false;
      z.panId = null;
    }
    if (zPointers.size === 2) {
      zStartPinch();
      return;
    }
    // single finger: only take over for panning once actually zoomed in.
    if (z.s > 1.01) {
      z.panId = e.pointerId;
      z.panTx = z.tx;
      z.panTy = z.ty;
      z.panX = e.clientX;
      z.panY = e.clientY;
      try {
        img.setPointerCapture(e.pointerId);
      } catch (_) {}
      img.classList.add('is-grabbing');
    }
  }

  /* ---- desktop drag-swipe helpers (mouse/pen at fit) ---------------------- */
  function lbSwipeStart(e) {
    lbSwipe = { id: e.pointerId, startX: e.clientX, startY: e.clientY, startScroll: rail.scrollLeft, moved: false };
  }
  function lbSwipeMove(e) {
    if (!lbSwipe || lbSwipe.id !== e.pointerId) return;
    var dx = e.clientX - lbSwipe.startX;
    var dy = e.clientY - lbSwipe.startY;
    if (!lbSwipe.moved) {
      if (Math.abs(dx) < Z_TAP_MOVE) return; // still within tap tolerance
      if (Math.abs(dy) > Math.abs(dx)) {
        lbSwipe = null;
        return;
      } // vertical intent — not a swipe
      lbSwipe.moved = true;
      rail.classList.add('is-dragging'); // freeze snap + kill smooth while dragging
      try {
        rail.setPointerCapture(lbSwipe.id);
      } catch (_) {}
    }
    // physical follow: scrollLeft-=dx tracks the pointer identically in LTR/RTL
    rail.scrollLeft = lbSwipe.startScroll - dx;
    e.preventDefault();
  }
  function lbSwipeEnd(e) {
    if (!lbSwipe || lbSwipe.id !== e.pointerId) return;
    var dx = e.clientX - lbSwipe.startX;
    var moved = lbSwipe.moved;
    lbSwipe = null;
    rail.classList.remove('is-dragging');
    if (!moved) return; // a tap — let the click through (zoom toggle etc.)
    swallowNextClick = true; // eat the trailing click (no zoom-toggle, no close)
    var rtl = getComputedStyle(document.documentElement).direction === 'rtl';
    var logical = rtl ? dx : -dx; // + = advance (next), direction-agnostic
    var threshold = Math.min(80, rail.clientWidth * 0.15);
    if (logical > threshold) goTo(activeIndex() + 1);
    else if (logical < -threshold) goTo(activeIndex() - 1);
    else goTo(activeIndex());
  }

  function zStartPinch() {
    var p = Array.from(zPointers.values());
    var a = p[0];
    var b = p[1];
    z.pinch = { d0: Math.hypot(a.x - b.x, a.y - b.y) || 1, s0: z.s };
    z.panId = null;
    if (z.img) z.img.classList.remove('is-grabbing');
    zSetZoomed(true); // own the gesture immediately (rail freeze + touch-action)
  }

  function onZoomPointerMove(e) {
    // desktop drag-swipe (at fit) is a separate pipeline from zoom/pan
    if (lbSwipe && lbSwipe.id === e.pointerId) {
      lbSwipeMove(e);
      return;
    }
    if (!z) return;
    var p = zPointers.get(e.pointerId);
    if (!p) return;
    p.x = e.clientX;
    p.y = e.clientY;
    if (!z.moved && (Math.abs(e.clientX - z.startX) > Z_TAP_MOVE || Math.abs(e.clientY - z.startY) > Z_TAP_MOVE)) {
      z.moved = true;
    }
    if (zPointers.size >= 2 && z.pinch) {
      e.preventDefault();
      var arr = Array.from(zPointers.values());
      var a = arr[0];
      var b = arr[1];
      var d = Math.hypot(a.x - b.x, a.y - b.y);
      var mx = (a.x + b.x) / 2;
      var my = (a.y + b.y) / 2;
      zTo(z.pinch.s0 * (d / z.pinch.d0), mx, my, false);
      z.didPinch = true;
      return;
    }
    if (z.panId === e.pointerId && z.s > 1.01) {
      e.preventDefault();
      var m = zMetrics();
      var c = zClamp(z.panTx + (e.clientX - z.panX), z.panTy + (e.clientY - z.panY), z.s, m);
      z.tx = c.tx;
      z.ty = c.ty;
      zApply(false);
    }
  }

  function onZoomPointerUp(e) {
    if (lbSwipe && lbSwipe.id === e.pointerId) {
      lbSwipeEnd(e);
      return;
    }
    zPointers.delete(e.pointerId);
    if (!z) return;
    if (z.pinch && zPointers.size < 2) {
      z.pinch = null;
      // a finger still down after a pinch → continue as a pan
      if (zPointers.size === 1 && z.s > 1.01) {
        var ent = Array.from(zPointers.entries())[0];
        z.panId = ent[0];
        z.panTx = z.tx;
        z.panTy = z.ty;
        z.panX = ent[1].x;
        z.panY = ent[1].y;
      }
    }
    if (z.panId === e.pointerId) {
      z.panId = null;
      if (z.img) {
        z.img.classList.remove('is-grabbing');
        try {
          z.img.releasePointerCapture(e.pointerId);
        } catch (_) {}
      }
    }
    if (zPointers.size === 0 && (z.moved || z.didPinch)) {
      swallowNextClick = true;
    }
  }

  function onZoomWheel(e) {
    if (!overImage(e)) return;
    if (!zEnsure()) return;
    // at fit, a horizontal-intent wheel/trackpad swipe should navigate the rail
    if (z.s <= 1.01 && Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;
    e.preventDefault();
    zTo(z.s * Math.exp(-e.deltaY * 0.0015), e.clientX, e.clientY, false);
  }

  /* ArrowLeft/Right are PHYSICAL directions; map to prev/next via dir so the
     keys always move the strip the way the arrow points. */
  function arrowStep(physical) {
    var rtl = getComputedStyle(document.documentElement).direction === 'rtl';
    goTo(activeIndex() + (rtl ? -physical : physical));
  }

  function activeIndex() {
    return state ? state.active : 0;
  }

  function goTo(i) {
    if (!state) return;
    // slide change resets zoom — also un-freezes the rail so scrollBy lands.
    if (z) zReset(false);
    var n = state.count;
    if (!n) return;
    // LOOP: wrap past either end instead of clamping (no dead-ends). A wrap
    // spans the whole rail, so jump it instantly rather than a long rewind.
    var wrapped = i < 0 || i >= n;
    i = ((i % n) + n) % n;
    var slide = state.slides[i];
    if (!slide) return;
    var r1 = rail.getBoundingClientRect();
    var r2 = slide.getBoundingClientRect();
    var delta = r2.left + r2.width / 2 - (r1.left + r1.width / 2);
    rail.scrollBy({ left: delta, behavior: REDUCED.matches || wrapped ? 'auto' : 'smooth' });
    setActive(i);
  }

  function syncActive() {
    if (!state) return;
    var r1 = rail.getBoundingClientRect();
    var center = r1.left + r1.width / 2;
    var best = 0;
    var bestDist = Infinity;
    state.slides.forEach(function (slide, i) {
      var r2 = slide.getBoundingClientRect();
      var d = Math.abs(r2.left + r2.width / 2 - center);
      if (d < bestDist) {
        bestDist = d;
        best = i;
      }
    });
    if (best !== state.active) setActive(best);
  }

  function setActive(i) {
    // safety net: if the active slide changes out from under a zoom, drop it
    if (z && z.img && z.s > 1.01 && state.slides[i] && z.img !== state.slides[i].querySelector('img')) {
      zReset(false);
    }
    state.active = i;
    // pause any video that is no longer the active slide
    state.slides.forEach(function (slide, j) {
      if (j !== i) {
        var v = slide.querySelector('video');
        if (v) v.pause();
      }
    });
    state.dots.forEach(function (dot, j) {
      dot.setAttribute('data-active', String(j === i));
    });
    // LOOP: arrows never dead-end — only disabled when there is nothing to loop.
    prevBtn.toggleAttribute('disabled', state.count <= 1);
    nextBtn.toggleAttribute('disabled', state.count <= 1);
  }

  function open(items, index) {
    if (!items || !items.length) return;
    ensureShell();
    if (closing) {
      dlg.classList.remove('is-closing');
      closing = false;
    }
    index = Math.max(0, Math.min(items.length - 1, index || 0));

    zTeardown(); // never inherit a prior open's zoom in the shared shell
    rail.innerHTML = '';
    dotsBox.innerHTML = '';
    state = { slides: [], dots: [], active: index, count: items.length };

    items.forEach(function (item, i) {
      var slide = document.createElement('div');
      slide.className = 'lx-lb__slide';
      if ((item.type || 'image') === 'video') {
        // video slide: inline <video controls> (no zoom); swipe/loop still apply
        slide.className += ' lx-lb__slide--video';
        var video = document.createElement('video');
        video.className = 'lx-lb__video';
        video.setAttribute('controls', '');
        video.setAttribute('playsinline', '');
        video.setAttribute('preload', 'metadata');
        if (item.poster) video.setAttribute('poster', item.poster);
        if (item.alt) video.setAttribute('aria-label', item.alt);
        video.src = item.src || '';
        slide.appendChild(video);
      } else {
        var img = document.createElement('img');
        img.src = item.src || '';
        if (item.srcset) img.setAttribute('srcset', item.srcset);
        img.setAttribute('sizes', item.sizes || '100vw');
        img.alt = item.alt || '';
        img.decoding = 'async';
        if (Math.abs(i - index) > 1) img.loading = 'lazy';
        slide.appendChild(img);
      }
      rail.appendChild(slide);
      state.slides.push(slide);

      var dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'lx-lb__dot';
      dot.setAttribute('aria-label', (T.slide || 'Slide') + ' ' + (i + 1));
      dot.addEventListener('click', function () {
        goTo(i);
      });
      dotsBox.appendChild(dot);
      state.dots.push(dot);
    });
    dotsBox.hidden = items.length <= 1;
    prevBtn.hidden = items.length <= 1;
    nextBtn.hidden = items.length <= 1;
    // "See it in Action" only when the page actually has a MediaBlazer to jump to.
    actionBtn.hidden = !document.querySelector('[data-mediablazer] [data-mb-tile]');

    if (!dlg.open) {
      ownsBodyLock = !document.body.classList.contains('overflow-hidden');
      document.body.classList.add('overflow-hidden');
      dlg.showModal();
    }

    // land on the requested photo before first paint of the open state
    var slide = state.slides[index];
    if (slide) rail.scrollTo({ left: slide.offsetLeft - (rail.clientWidth - slide.clientWidth) / 2 });
    // RTL: offsetLeft math flips — snap through rect delta instead
    var r1 = rail.getBoundingClientRect();
    var r2 = slide.getBoundingClientRect();
    var delta = r2.left + r2.width / 2 - (r1.left + r1.width / 2);
    if (Math.abs(delta) > 1) rail.scrollBy({ left: delta });
    setActive(index);

    if (REDUCED.matches) {
      dlg.classList.add('is-open');
    } else {
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          dlg.classList.add('is-open');
        });
      });
    }
    try {
      dlg.focus({ preventScroll: true });
    } catch (e) {
      dlg.focus();
    }
  }

  function close() {
    if (!dlg || !dlg.open || closing) return;
    closing = true;
    // stop any playing video before the shell hides
    if (state) {
      state.slides.forEach(function (slide) {
        var v = slide.querySelector('video');
        if (v) v.pause();
      });
    }
    dlg.classList.remove('is-open');
    dlg.classList.add('is-closing');
    var done = function () {
      dlg.classList.remove('is-closing');
      closing = false;
      if (dlg.open) dlg.close();
      if (ownsBodyLock) document.body.classList.remove('overflow-hidden');
      zTeardown();
    };
    if (REDUCED.matches) done();
    else setTimeout(done, 240);
  }

  function chip(host) {
    if (!host || host.querySelector(':scope > .lx-lb-zoomchip')) return;
    host.classList.add('lx-lb-zoomhost');
    var el = document.createElement('span');
    el.className = 'lx-lb-zoomchip';
    el.setAttribute('aria-hidden', 'true');
    el.innerHTML = SVG.zoom;
    host.appendChild(el);
  }

  // Exposed under both casings: LxLightbox (existing callers) and LXLightbox
  // (Build A's product gallery + feed tiles) resolve to the same instance.
  window.LxLightbox = { open: open, close: close, chip: chip };
  window.LXLightbox = window.LxLightbox;

  /* ======================================================================== */
  /* Product-page gallery adapter ([data-lx-gallery], lx-main-product)        */
  /* ======================================================================== */

  function wireProductGallery() {
    var gallery = document.querySelector('[data-lx-gallery]');
    if (!gallery || gallery.hasAttribute('data-lx-lb-wired')) return;
    gallery.setAttribute('data-lx-lb-wired', 'true');

    var viewport = gallery.querySelector('[data-lx-gal-viewport]');
    var track = gallery.querySelector('[data-lx-gal-track]');
    var slides = Array.prototype.slice.call(gallery.querySelectorAll('[data-lx-gal-slide]'));
    if (!viewport || !track || !slides.length) return;

    // images only (videos/iframes keep their own inline players)
    var items = [];
    var slideToItem = {}; // gallery slide index -> lightbox item index
    slides.forEach(function (slide, i) {
      if (slide.querySelector('video, iframe')) return;
      var img = slide.querySelector('img');
      if (!img) return;
      slideToItem[i] = items.length;
      items.push({
        src: img.currentSrc || img.getAttribute('src') || '',
        srcset: img.getAttribute('srcset') || '',
        alt: img.getAttribute('alt') || ''
      });
      window.LxLightbox.chip(slide);
    });
    if (!items.length) return;

    function currentSlideIndex() {
      // track rests at translateX(-<index*100>%) (set by lx-product.js)
      var t = track.style.transform || '';
      var m = t.match(/translateX\(\s*(-?\d+(?:\.\d+)?)%\s*\)/);
      if (m) return Math.round(Math.abs(parseFloat(m[1])) / 100);
      var start = parseInt(track.getAttribute('data-start-index') || '0', 10);
      return isNaN(start) ? 0 : start;
    }

    // lx-product.js pointer-captures the viewport on pointerdown, retargeting
    // the click — so remember where the press started and how far it moved.
    var press = null;
    viewport.addEventListener(
      'pointerdown',
      function (e) {
        var slide = e.target && e.target.closest ? e.target.closest('[data-lx-gal-slide]') : null;
        press = {
          x: e.clientX,
          y: e.clientY,
          onImage: !!(slide && !slide.querySelector('video, iframe') && slide.querySelector('img')),
          button: !!(e.target.closest && e.target.closest('button'))
        };
      },
      true
    );
    viewport.addEventListener('click', function (e) {
      if (!press) return;
      var moved = Math.abs(e.clientX - press.x) > 8 || Math.abs(e.clientY - press.y) > 8;
      var ok = press.onImage && !press.button && !moved;
      press = null;
      if (!ok) return;
      var idx = currentSlideIndex();
      var itemIdx = slideToItem[idx];
      if (itemIdx == null) return;
      window.LxLightbox.open(items, itemIdx);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', wireProductGallery);
  } else {
    wireProductGallery();
  }
})();
